// Community.jsx - Migrated to Tailwind + shadcn/ui
// This form component has been migrated from MUI to use shadcn/ui components
// The original functionality is preserved with Input, Select, and Button components
// Import and usage updated to match the new component library

import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import api from "../../services/api";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/auth/AuthState";

const Community = ({ formStates, setFormStates, nextStep, setviewPrev, setviewNext, setalert }) => {
  const { community_id } = useParams();
  const { currentUser } = useAuth();

  const handleChange = (field, value) => {
    setFormStates((prev) => ({
      ...prev,
      community: { ...prev.community, [field]: value },
    }));
  };

  // Check if we're editing an existing community
  const isEditMode = Boolean(community_id);

  const handleSubmit = async () => {
    try {
      let response;
      if (isEditMode) {
        // Update existing community - add user_id to payload
        const updatePayload = {
          ...formStates.community,
          user_id: currentUser.user_id
        };
        response = await api.post("/community/update", updatePayload);
        if (response.status === 200) {
          setalert({ msg: "Community updated successfully", type: "success" });
          nextStep();
        }
      } else {
        // Create new community
        response = await api.post("/community/create", formStates.community);
        if (response.status === 200) {
          setalert({ msg: "Community created successfully", type: "success" });
          nextStep();
        }
      }
    } catch (error) {
      setalert({
        msg: error.response?.data || `Failed to ${isEditMode ? 'update' : 'create'} community`,
        type: "error"
      });
    }
  };

  React.useEffect(() => {
    setviewPrev(false);
    setviewNext(true);
  }, []);


  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Community Details</h2>
        <p className="text-gray-600">
          Set up your community's basic information and location
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="community_name" className="text-base font-semibold">
            Community Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="community_name"
            value={formStates.community.community_name}
            onChange={(e) => handleChange("community_name", e.target.value)}
            required
            className="mt-1"
            placeholder="e.g., Sunrise Fitness Club"
          />
        </div>

        <div>
          <Label htmlFor="description" className="text-base font-semibold">
            Description
          </Label>
          <Textarea
            id="description"
            value={formStates.community.description}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={3}
            className="mt-1"
            placeholder="Describe your community's purpose and goals..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="locality" className="text-base font-semibold">
              Locality
            </Label>
            <Input
              id="locality"
              value={formStates.community.locality}
              onChange={(e) => handleChange("locality", e.target.value)}
              className="mt-1"
              placeholder="e.g., Downtown"
            />
          </div>
          <div>
            <Label htmlFor="pincode" className="text-base font-semibold">
              Pincode
            </Label>
            <Input
              id="pincode"
              value={formStates.community.pincode}
              onChange={(e) => handleChange("pincode", e.target.value)}
              className="mt-1"
              placeholder="e.g., 400001"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city" className="text-base font-semibold">
              City <span className="text-red-500">*</span>
            </Label>
            <Input
              id="city"
              value={formStates.community.city}
              onChange={(e) => handleChange("city", e.target.value)}
              required
              className="mt-1"
              placeholder="e.g., Mumbai"
            />
          </div>
          <div>
            <Label htmlFor="state" className="text-base font-semibold">
              State <span className="text-red-500">*</span>
            </Label>
            <Input
              id="state"
              value={formStates.community.state}
              onChange={(e) => handleChange("state", e.target.value)}
              required
              className="mt-1"
              placeholder="e.g., Maharashtra"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="country" className="text-base font-semibold">
            Country <span className="text-red-500">*</span>
          </Label>
          <Input
            id="country"
            value={formStates.community.country}
            onChange={(e) => handleChange("country", e.target.value)}
            required
            className="mt-1"
            placeholder="e.g., India"
          />
        </div>

        <div>
          <Label htmlFor="access" className="text-base font-semibold">
            Access Type
          </Label>
          <Select
            value={formStates.community.access}
            onValueChange={(value) => handleChange("access", value)}
          >
            <SelectTrigger id="access" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Open">Open - Anyone can join</SelectItem>
              <SelectItem value="Closed">Closed - Invite only</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500 mt-1">
            {formStates.community.access === "Open"
              ? "Anyone with the join code can join your community"
              : "Only invited members can join your community"}
          </p>
        </div>
      </div>

      <Button onClick={handleSubmit} className="w-full" size="lg">
        {isEditMode ? "Update" : "Create"} Community
      </Button>
    </div>
  );
};

export default Community;
